// The fields an expense and its template share: its category (an existing one, or a new one named here, TD-041),
// whose it is (no client is overhead) and its service line. Native selects, as the other finance modals, one
// control per label. Plus what an expense modal says when its lists could not load.
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { PercentInput } from "../components/PriceSplitEditor";
import { Required } from "../components/Required";
import { WithoutIva } from "../components/WithoutIva";
import type { ClientSummary, ExpenseCategory, PassBilling, ServiceLine } from "../types";
import { NEW_CATEGORY, type CategoryChoice } from "../utils/expenses";
import { MARKUP_PCT_MAX, type PassForm } from "../utils/passedCosts";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const clientsWord = ENTITY_CONFIG.client.plural.toLowerCase();

/** The category select, and under it its own labelled field naming a new one once "New category…" is picked. */
export function CategoryField({ categories, value, onChange, error }: { categories: ExpenseCategory[]; value: CategoryChoice; onChange: (c: CategoryChoice) => void; error?: string }) {
  const picked = value.id ?? NEW_CATEGORY;
  return (
    <div className="space-y-3">
      <FormField label={ENTITY_CONFIG.expenseCategory.label}>
        <select
          className={inputClassName}
          value={picked}
          onChange={(e) => onChange(e.target.value === NEW_CATEGORY ? { id: null, newLabel: "" } : { id: e.target.value, newLabel: "" })}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
          <option value={NEW_CATEGORY}>New category…</option>
        </select>
      </FormField>
      {picked === NEW_CATEGORY && (
        <FormField label={<Required>New category</Required>} error={error}>
          <input className={inputClassName} aria-required="true" value={value.newLabel} placeholder="e.g. Software" onChange={(e) => onChange({ id: null, newLabel: e.target.value })} />
        </FormField>
      )}
    </div>
  );
}

export function ClientField({ clients, value, onChange }: { clients: ClientSummary[]; value: string; onChange: (clientId: string) => void }) {
  const choices = clients.filter((c) => c.status !== "ARCHIVED" || c.id === value).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <FormField label={ENTITY_CONFIG.client.label} hint={`None: an overhead, no ${clientWord}'s cost`}>
      <select className={inputClassName} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">None · overhead</option>
        {choices.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </FormField>
  );
}

export function ServiceLineField({ lines, value, onChange }: { lines: ServiceLine[]; value: string; onChange: (serviceLineId: string) => void }) {
  return (
    <FormField label={ENTITY_CONFIG.serviceLine.label}>
      <select className={inputClassName} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {lines.map((l) => (
          <option key={l.id} value={l.id}>{l.label}</option>
        ))}
      </select>
    </FormField>
  );
}

/**
 * "Pass along to the client" (TD-100): how it is billed, at cost, plus a percentage, or plus a fixed markup. It names the client alone
 * and rides the client's next invoice (finance consolidation answer d), so no contract is picked. Only with a client picked: an
 * overhead has nobody to pass it to. The markup's error sits under its field. `required`: always on, no checkbox (a bill's modal).
 * `billingLocked`: an invoice billed it, so it stays as invoiced. `own`: the agency's own client, never billed (TD-105), so one
 * line says the cost stays the agency's own and nothing is offered.
 */
export function PassAlongField({ clientId, own, value, onChange, markupError, required = false, billingLocked = false }: {
  clientId: string;
  /** The picked client is the agency's own, from its contracts query: false while it loads. */
  own: boolean;
  value: PassForm;
  onChange: (p: PassForm) => void;
  markupError?: string;
  required?: boolean;
  billingLocked?: boolean;
}) {
  if (own) return <p className="text-xs text-gray-500">The agency's own profile is never billed, so this cost stays the agency's own expense.</p>;
  const on = required || (value.on && !!clientId);
  const radio = (billing: PassBilling, label: string) => (
    <label className={billingLocked ? "flex items-center gap-1.5 text-gray-400" : "flex items-center gap-1.5 cursor-pointer"}>
      <input type="radio" name="pass-billing" checked={value.billing === billing} onChange={() => onChange({ ...value, billing })} />
      {label}
    </label>
  );
  return (
    <div className="space-y-3">
      {!required && (
        <label className={clientId ? "flex items-center gap-2 text-sm text-gray-700 cursor-pointer" : "flex items-center gap-2 text-sm text-gray-400"}>
          <input type="checkbox" checked={value.on && !!clientId} disabled={!clientId} onChange={(e) => onChange({ ...value, on: e.target.checked })} />
          Pass along to the {clientWord}
        </label>
      )}
      {!required && !clientId && <p className="text-xs text-gray-400">Pick a {clientWord} first: a cost passed along rides their next invoice.</p>}
      {on && clientId && (
        <div className="space-y-2">
          <fieldset disabled={billingLocked}>
            <legend className="text-xs font-medium text-gray-600 mb-1">Billed to the {clientWord}{billingLocked ? ", as invoiced" : ""}</legend>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-1.5 text-sm text-gray-700">
              {radio("at_cost", "At cost")}
              {radio("percent", "Plus a % markup")}
              {radio("fixed", "Plus a fixed markup")}
            </div>
          </fieldset>
          {value.billing === "percent" && (
            <FormField label="Markup" error={markupError}>
              <PercentInput value={value.pct} onChange={(pct) => onChange({ ...value, pct })} max={MARKUP_PCT_MAX} ariaLabel="Markup percent" disabled={billingLocked} />
            </FormField>
          )}
          {value.billing === "fixed" && (
            <FormField label="Markup" error={markupError}>
              <span className="block w-32">
                <MoneyInput valueMinor={value.fixed} currency="EUR" onChange={(fixed) => onChange({ ...value, fixed })} invalid={!!markupError && value.fixed !== null} disabled={billingLocked} ariaLabel="Markup amount" />
              </span>
              {/* An amount typed, net of IVA like every finance amount (TD-101). */}
              <WithoutIva />
            </FormField>
          )}
        </div>
      )}
    </div>
  );
}

/** Beside Retry in an expense modal (ModalPending) whose lists could not load. */
export const EXPENSE_LISTS_FAILED = `Could not load the categories, ${clientsWord} or service lines.`;
